// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useState, useRef, useEffect } from 'react';
import InterviewCard from '../../../components/Interview/InterviewCard';
import ProgressIndicator from '../../../components/Interview/ProgressIndicator';
import Button from '../../../components/Interview/shared/Button';
import { interviewAPI } from '../../../services/interview/api';
import { VerifyOTPResponse } from '../../../types/interview';
import './OTPVerification.css';

interface OTPVerificationProps {
  email: string;
  sessionToken: string;
  onNext: () => void;
  onResend: () => Promise<void>;
}

const OTPVerification: React.FC<OTPVerificationProps> = ({ email, sessionToken, onNext, onResend }) => {
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [timer, setTimer] = useState(120); // 2 minutes
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleChange = (index: number, value: string): void => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError(null);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent): void => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    pastedData.split('').forEach((char, index) => {
      if (index < 6) newOtp[index] = char;
    });
    setOtp(newOtp);

    const nextEmptyIndex = newOtp.findIndex((digit) => !digit);
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async (): Promise<void> => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response: VerifyOTPResponse = await interviewAPI.verifyOTP({
        token: sessionToken,
        otp: otpString
      });

      if (response.verified) {
        setSuccess('Verification successful!');
        setTimeout(() => {
          onNext();
        }, 1000);
      } else {
        setError(response.message || 'Invalid verification code');
        setOtp(new Array(6).fill(''));
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('Verify OTP error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async (): Promise<void> => {
    if (!canResend) return;

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await onResend();
      setTimer(120);
      setCanResend(false);
      setSuccess('Verification code resent successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="identity-container">
      <InterviewCard>
        <ProgressIndicator totalSteps={3} currentStep={2} />

        <h2 className="otp-heading">Check your email</h2>
        <p className="otp-subheading">We sent a 6-digit verification code to:</p>

        <div style={{ textAlign: 'center' }}>
          <span className="otp-emailBadge">
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            <span>{email}</span>
          </span>
        </div>

        {error && <div className="otp-errorMessage">{error}</div>}
        {success && <div className="otp-successMessage">{success}</div>}

        <div className="otp-otpContainer" onPaste={handlePaste}>
          {otp.map((digit, index) => {
            const inputClass = digit ? 'otp-otpInput otp-filled' : 'otp-otpInput';
            return (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={inputClass}
                autoFocus={index === 0}
              />
            );
          })}
        </div>

        {timer > 0 && (
          <div className="otp-timer">
            <svg
              className="otp-timerIcon"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <span>Code expires in </span>
            <span style={{ color: 'var(--interview-brand-primary)' }}>{formatTime(timer)}</span>
          </div>
        )}

        <Button onClick={handleVerify} disabled={isLoading || otp.some((d) => !d)}>
          {isLoading ? 'Verifying...' : 'Verify & continue'}
        </Button>

        <div className="otp-resendSection">
          Didn't receive it?{' '}
          <span className={canResend ? 'otp-resendLink' : 'otp-resendLink otp-disabled'} onClick={handleResend}>
            Resend code
          </span>
          . Check your spam folder if it doesn't arrive.
        </div>
      </InterviewCard>
    </div>
  );
};

export default OTPVerification;
